import { geoServer } from "../services/geoServer";

interface DescribeFeatureTypeResponse {
  featureTypes?: {
    properties?: { name?: string; type?: string; localType?: string }[];
  }[];
}

const GEOMETRY_LOCAL_TYPES = new Set([
  "Point",
  "MultiPoint",
  "LineString",
  "MultiLineString",
  "Curve",
  "MultiCurve",
  "Polygon",
  "MultiPolygon",
  "Surface",
  "MultiSurface",
  "Geometry",
  "GeometryCollection",
]);

async function describeGeometryField(typeName: string): Promise<string> {
  const response = await geoServer.get<DescribeFeatureTypeResponse>("wfs", {
    params: {
      service: "WFS",
      version: "2.0.0",
      request: "DescribeFeatureType",
      typeNames: typeName,
      outputFormat: "application/json",
    },
  });
  const properties = response.data?.featureTypes?.[0]?.properties ?? [];
  const field = properties.find(
    (property) =>
      property.localType && GEOMETRY_LOCAL_TYPES.has(property.localType)
  )?.name;
  if (!field) {
    throw new Error(`Nenhuma coluna de geometria reconhecida em ${typeName}`);
  }
  return field;
}

/**
 * Name of the geometry column, per `workspace:layer`. WFS needs it to build
 * spatial CQL predicates and it isn't part of our layer catalog, so we ask
 * GeoServer once and reuse the answer for the rest of the session.
 *
 * Rejects when the field can't be discovered — callers must fail closed
 * instead of falling back to a request without a spatial predicate. Failed
 * lookups are evicted so the next call retries.
 */
const geometryFieldCache = new Map<string, Promise<string>>();

export function getGeometryField(typeName: string): Promise<string> {
  let cached = geometryFieldCache.get(typeName);
  if (!cached) {
    cached = describeGeometryField(typeName).catch((error) => {
      geometryFieldCache.delete(typeName);
      console.error(
        `[geometryField] Não foi possível descobrir o campo de geometria de ${typeName}:`,
        error
      );
      throw error;
    });
    geometryFieldCache.set(typeName, cached);
  }
  return cached;
}
